import type { Point, Rect } from '@geom';
import { LayoutConstants } from '@layout';
import { clamp } from '@utils';
import type { CommandContext } from './CommandContext';

export function canZoomToFit(context: CommandContext): boolean {
  const { doc } = context;
  return !doc.graph.isEmpty;
}

/**
 * Helper function to calculate zoom-to-fit viewport state for a document bounds and viewport rectangle.
 * This is used both for commands and for initial document setup.
 *
 * @param docBounds - The bounds of the document
 * @param elemBounds - The viewport bounds in element space
 * @param maxZoom - Maximum allowed zoom level
 * @param minZoom - Minimum allowed zoom level
 * @returns The calculated zoom and pan values, or undefined if no bounds available
 */
export function calculateViewportForBounds(
  docBounds: Rect | undefined,
  elemBounds: Rect,
  maxZoom: number,
  minZoom: number
): { zoom: number; pan: Point } | undefined {
  if (docBounds === undefined) {
    return undefined;
  }

  // Element viewport rectangle after accounting for padding
  const elemViewportRect = elemBounds
    .aligningTopLeftToOrigin()
    .shrinkingBy(LayoutConstants.PAN_ZOOM_REVEAL_PADDING);

  // Calculate the zoom required to fit the document bounds into the viewport
  const requiredZoomX = elemViewportRect.width / docBounds.width;
  const requiredZoomY = elemViewportRect.height / docBounds.height;
  const requiredZoom = Math.min(requiredZoomX, requiredZoomY);

  // Clamp zoom to the allowed range
  const zoom = clamp(requiredZoom, [minZoom, maxZoom]);

  // Compute the pan to center the document in the viewport at the calculated zoom
  const scaledDocCenter = docBounds.center.mul(zoom);
  const pan = elemViewportRect.center.sub(scaledDocCenter);

  return { zoom, pan };
}

export function calculateZoomToFit(
  context: CommandContext
): { zoom: number; pan: Point } | undefined {
  if (!canZoomToFit(context)) {
    return undefined;
  }

  const { doc, elemBounds, maxZoom, minZoom } = context;
  return calculateViewportForBounds(doc.bounds, elemBounds, maxZoom, minZoom);
}

export function zoomToFit(
  context: CommandContext
): { zoom: number; pan: Point } | undefined {
  const result = calculateZoomToFit(context);
  context.update(result);
  return result;
}

export function zoom(
  newZoom: number,
  elemPoint: Point | undefined,
  context: CommandContext
): { zoom: number; pan: Point } | undefined {
  const { pan, zoom: oldZoom } = context;
  const elemBounds = context.elemBounds;

  // Convert to element coordinates by subtracting elemBounds.topLeft
  const elemOffset = elemBounds.topLeft;

  // Adjust elemPoint and elemBounds.center to be in element coordinates
  const elemPointInElementCoords = elemPoint ? elemPoint.sub(elemOffset) : undefined;
  const elemNewCenter = elemPointInElementCoords ?? elemBounds.center.sub(elemOffset);

  // Compute the document point corresponding to the element point
  const docPoint = elemNewCenter.sub(pan).div(oldZoom);

  // Calculate the new pan to keep the docPoint under the same elemPoint after zoom
  const newPan = elemNewCenter.sub(docPoint.mul(newZoom));

  const result = { zoom: newZoom, pan: newPan };
  console.log('zoom', result);

  context.update(result);
  return result;
}

/**
 * Centers the viewport on a given document point.
 *
 * @param docPoint - The point in document coordinates to center on.
 * @param context - The command context containing necessary state.
 * @returns An object with the new pan value.
 */
export function centerOnPoint(
  docPoint: Point | undefined,
  context: CommandContext
): { pan: Point } | undefined {
  if (!docPoint) {
    return;
  }
  const { zoom, elemBounds } = context;
  const elemCenter = elemBounds.center;
  const pan = elemCenter.sub(docPoint.mul(zoom));
  const result = { pan };
  context.update(result);
  return result;
}

/**
 * Adjusts the view to center on the currently selected elements.
 *
 * @param context - The command context containing the current selection and document.
 * @returns An object containing the new pan position and zoom level, or undefined if the selection is invalid.
 */
export function focusSelection(
  context: CommandContext
): { pan: Point; zoom: number } | undefined {
  const { selection, elemBounds, zoom: currentZoom, maxZoom } = context;
  const bounds = context.doc.elementsBounds(selection);
  if (!bounds) {
    return undefined;
  }

  const padding = LayoutConstants.PAN_ZOOM_REVEAL_PADDING;

  // Element viewport rectangle with padding
  const elemViewportRect = elemBounds.aligningTopLeftToOrigin().shrinkingBy(padding);

  // Calculate required zoom to fit selection bounds
  const requiredZoomX = elemViewportRect.width / bounds.width;
  const requiredZoomY = elemViewportRect.height / bounds.height;
  const requiredZoom = Math.min(requiredZoomX, requiredZoomY);

  // Only zoom out if necessary
  const newZoom = Math.min(currentZoom, requiredZoom, maxZoom);

  // Compute new pan to center the selection
  const scaledSelectionCenter = bounds.center.mul(newZoom);
  const pan = elemViewportRect.center.sub(scaledSelectionCenter);

  const result = { pan, zoom: newZoom };
  context.update(result);
  return result;
}

/**
 * Calculates the adjusted pan and zoom of the viewport in the context to reveal
 * the specified bounds rectangle.
 *
 * @param bounds - The rectangular bounds that need to be revealed.
 * @param context - The command context containing the current zoom, pan, and
 * element bounds.
 * @returns An object containing the new pan and zoom values.
 */
export function revealBounds(
  bounds: Rect,
  context: CommandContext
): { pan: Point; zoom: number } {
  const { zoom: currentZoom, pan: currentPan, elemBounds } = context;
  const padding = LayoutConstants.PAN_ZOOM_REVEAL_PADDING; // in element units

  // Compute the element viewport rectangle (in element space) after accounting for padding
  const elemViewportRect = elemBounds.aligningTopLeftToOrigin().shrinkingBy(padding);

  // Compute the required zoom to fit bounds into the available viewport with padding
  const requiredZoomX = elemViewportRect.width / bounds.width;
  const requiredZoomY = elemViewportRect.height / bounds.height;
  const requiredZoom = Math.min(requiredZoomX, requiredZoomY);

  // Only zoom out; never zoom in
  const newZoom = Math.min(currentZoom, requiredZoom);

  // Recompute the viewport rectangle in document space with the new zoom
  const newViewportRect = elemViewportRect.sub(currentPan).mul(1 / newZoom);

  // Compute the delta needed to reveal bounds in the new viewport
  const delta = newViewportRect.revealDelta(bounds);

  // Adjust pan to move the viewport by delta scaled back to element space
  const newPan = currentPan.sub(delta.mul(newZoom));

  return { pan: newPan, zoom: newZoom };
}
